'use client';
import {
  Input,
  VStack,
  Textarea,
  InputGroup,
  useBoolean,
  InputRightElement,
} from '@chakra-ui/react';
import { IMaskInput } from 'react-imask';
import { memo, useEffect, useRef } from 'react';

import LabelText from 'shared/InputsLabelText';
import ErrorText from 'shared/InputsErrorText';
import NextImage from 'shared/NextImage';

import Cross from 'assets/icons/cross.svg';
import { scrollBarStyle } from 'constants/scrollBarStyle';
import { inputsWrapperStyle } from 'constants/inputsWrappersStyles';
import '../style.css';

const sameInputsProps = { cursor: 'pointer', border: 'none', focusBorderColor: 'ourWhite' };

const TextField = ({
  inputRef,
  label = '',
  hint = '',
  error = false,
  type = 'input',
  onChange,
  value,
  max = undefined,
  maxH = null,
  minH = null,
  mask = null,
  maskChar = '_',
  trigger = null,
  ...props
}) => {
  const input = useRef(null);

  const setRefs = node => {
    input.current = node;
    inputRef(node);
  };

  const handleFocusInput = () =>
    type === 'date' ? input.current.element?.focus() : input.current?.focus();

  const handleClear = () => onChange('');
  const handleInputOnBlur = ({ target: { value } }) => onChange(value.trim());
  const handleInputChange = ({ target }) => {
    if (target.value.length > max) return;
    onChange(target.value);
  };

  useEffect(() => {
    return () => trigger && trigger(props.name);
  }, [props.name, trigger]);

  return (
    <VStack w="full" align="start" gap="0" position="relative">
      <LabelText value={value} label={label} />

      <InputGroup role="group" onClick={handleFocusInput} sx={inputsWrapperStyle(type, error)}>
        {type === 'date' ? (
          <DateMaskedInput
            value={value}
            inputRef={setRefs}
            onChange={onChange}
            handleClear={handleClear}
            {...props}
          />
        ) : type === 'textarea' ? (
          <Textarea
            value={value}
            ref={setRefs}
            maxH={maxH}
            minH={minH}
            {...sameInputsProps}
            sx={scrollBarStyle}
            onInput={handleInputChange}
            onBlur={handleInputOnBlur}
            {...props}
          />
        ) : (
          <Input
            id={props.name}
            ref={setRefs}
            value={value}
            type={type}
            {...sameInputsProps}
            onInput={handleInputChange}
            onBlur={handleInputOnBlur}
            {...props}
          />
        )}

        <InputRightElement
          mt={type === 'textarea' ? '4px' : '0'}
          boxSize="16px"
          pos="relative"
          opacity={value?.length ? 1 : 0}
          pointerEvents={value?.length ? 'all' : 'none'}
          onClick={handleClear}
        >
          <NextImage src={Cross} alt="Крестик" w="full" h="full" />
        </InputRightElement>
      </InputGroup>

      <ErrorText error={error} hint={hint} />
    </VStack>
  );
};

export default memo(TextField);

const DateMaskedInput = ({ inputRef, value, onChange, handleClear, ...props }) => {
  const [isFocused, setIsFocused] = useBoolean(false);
  const showMask = isFocused || value;

  return (
    <Input
      as={IMaskInput}
      ref={inputRef}
      value={value}
      unmask={false}
      mask="00.00.0000"
      // @ts-ignore
      lazy={!showMask}
      {...sameInputsProps}
      onFocus={setIsFocused.on}
      onBlur={() => {
        setIsFocused.off();
        if (value === '__.__.____') handleClear();
      }}
      onAccept={maskedValue => onChange(maskedValue)}
      {...props}
    />
  );
};
